// User Roles - matching backend
export enum UserRole {
  SUPER_ADMIN = 'superadmin',
  PMO = 'pmo',
  MANAGER = 'manager',
  EMPLOYEE = 'employee',
  IT_ADMIN = 'it_admin',
}

// Backend permissions structure - using Record for dynamic keys
// Permission keys use dot notation: 'timesheets.fill', 'users.create', etc.
export type UserPermissions = Record<string, boolean>;

// Department timesheet access structure
export interface DepartmentAccessEntry {
  department_id: number;
  department_name: string | null;
  can_view: boolean;
  can_approve: boolean;
  can_reject: boolean;
  can_update: boolean;
}

export interface DepartmentTimesheetAccess {
  departments: DepartmentAccessEntry[];
  has_any_view: boolean;
  has_any_approve: boolean;
  has_any_reject: boolean;
  has_any_update: boolean;
}

// User interface - matching backend response
export interface User {
  id: number;
  email: string;
  name: string;
  employee_code: string;
  job_position?: string | null;
  category?: string | null;
  department?: string | null;
  department_id?: number | null;
  manager?: string | null;
  manager_id?: number | null;
  roles: string[];
  permissions: UserPermissions;
  department_timesheet_access?: DepartmentTimesheetAccess;
  is_active: boolean;
  must_change_password: boolean;
  last_login?: string | null;
  created_at: string;
}

// For backwards compatibility
export interface LegacyUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatar?: string;
  department?: string;
  createdAt: string;
  updatedAt: string;
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  message: string;
  access_token: string;
  refresh_token: string;
  user: User;
  must_change_password: boolean;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Role interface - matching backend
export interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  permissions: UserPermissions;
  is_system_role: boolean;
  is_active: boolean;
  created_at: string;
}

// Department interface - matching backend
export interface Department {
  id: number;
  name: string;
  is_active: boolean;
  user_count: number;
  created_at: string;
}

// API Response types
export interface UsersListResponse {
  users: User[];
  total: number;
  pages: number;
  current_page: number;
}

export interface RolesListResponse {
  roles: Role[];
}

export interface DepartmentsListResponse {
  departments: Department[];
}

export interface PermissionItem {
  key: string;
  label: string;
  description: string;
}

// Dashboard types
export interface DashboardCard {
  id: string;
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon?: string;
}

export interface TimesheetEntry {
  id: string;
  userId: string;
  projectId: string;
  projectName: string;
  date: string;
  hours: number;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  name: string;
  code: string;
  description?: string;
  startDate: string;
  endDate?: string;
  status: 'active' | 'completed' | 'on_hold';
  managerId: string;
  teamMembers: string[];
}

// Dashboard data types for each role
export interface SuperAdminDashboardData {
  totalUsers: number;
  totalProjects: number;
  activeProjects: number;
  totalHoursLogged: number;
  totalHoursAllTime: number;
  pendingApprovals: number;
  recentActivity: ActivityItem[];
  systemHealth: SystemHealth;
  usersByRole: RoleCount[];
  hoursTrend: Array<{ month: string; year: number; billable: number; unbillable: number; total: number }>;
  projectStatusDistribution: Array<{ status: string; count: number }>;
  timesheetStatusDistribution: Array<{ status: string; count: number }>;
  settingsChanges: AuditLogEntry[];
  permissionsChanges: AuditLogEntry[];
}

export interface PMODashboardData {
  totalProjects: number;
  activeProjects: number;
  totalClients: number;
  totalHoursThisMonth: number;
  billableHoursThisMonth: number;
  unbillableHoursThisMonth: number;
  hoursTrend: Array<{ month: string; year: number; billable: number; unbillable: number; total: number }>;
  projectTypeDistribution: Array<{ type: string; count: number }>;
  timesheetStatusDistribution: Array<{ status: string; count: number }>;
  projectsOverview: ProjectOverview[];
  resourceUtilization: number;
  pendingTimesheets: number;
  pendingProjectRequests: number;
  pendingLateEntries: number;
  unassignedEmployees: number;
  recentProjectRequests: Array<{
    id: number;
    employeeName: string;
    employeeId: number;
    projectName: string;
    projectId: number;
    reason: string | null;
    createdAt: string | null;
  }>;
  recentLateEntries: Array<{
    id: number;
    employeeName: string;
    employeeId: number;
    requestedDate: string | null;
    reason: string;
    createdAt: string | null;
  }>;
  pendingWorkRequests: number;
  benchEmployeeCount: number;
  totalBenchHours: number;
  billableProjectsCount: number;
  topBillableProjects: Array<{
    projectId: string;
    projectName: string;
    clientName: string | null;
    totalHours: number;
  }>;
  topResourcesByBillableHours: Array<{
    employeeId: number;
    employeeName: string;
    department: string | null;
    totalBillableHours: number;
  }>;
}

export interface ManagerDashboardData {
  // Stat cards
  totalProjects: number;
  teamSize: number;
  pendingApprovals: number;
  totalHoursThisMonth: number;
  // Red flags
  redFlags: RedFlag[];
  // Pending approvals list
  pendingApprovalsList: PendingApprovalItem[];
  // Billable / Non-billable
  billableHoursThisMonth: number;
  unbillableHoursThisMonth: number;
  hoursTrend: Array<{ month: string; year: number; billable: number; unbillable: number; total: number }>;
  // Projects
  projectsOverview: ManagerProjectOverview[];
  projectTypeDistribution: Array<{ type: string; count: number }>;
  // Activity
  activities: ManagerActivityItem[];
  // Work requests
  pendingWorkRequests?: number;
}

export interface RedFlag {
  type: 'overdue' | 'tnm_exceeded' | 'tnm_warning' | 'ending_soon' | 'zero_hours';
  severity: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  projectId: number | null;
  projectName: string | null;
}

export interface PendingApprovalItem {
  id: number;
  employeeName: string;
  employeeId: number;
  projectName: string;
  projectId: number;
  date: string;
  hours: number;
  description: string;
  status: string;
  createdAt: string;
}

export interface ManagerProjectOverview {
  projectId: number;
  projectName: string;
  projectType: string;
  isBillable: boolean;
  totalHoursThisMonth: number;
  monthlyBudget: number | null;
  teamCount: number;
  status: string;
  startDate: string | null;
  endDate: string | null;
  clientName: string | null;
}

export interface ManagerActivityItem {
  id: string;
  type: 'new_project' | 'new_resource' | 'ending_soon';
  message: string;
  detail: string;
  timestamp: string;
  projectId: number | null;
  projectName: string | null;
}

export interface ProjectHours {
  projectId: number;
  projectName: string;
  hours: number;
  color: string;
}

export interface DailyChartData {
  date: string;
  totalHours: number;
  projects: ProjectHours[];
  isWeekend: boolean;
  isHoliday?: boolean;
  holidayName?: string;
}

export interface EmployeeDashboardData {
  hoursThisWeek: number;
  hoursThisPeriod: number;
  daysMissing: number;
  periodStart: string;
  periodEnd: string;
  pendingTimesheets: number;
  approvedTimesheets: number;
  assignedProjects: Project[];
  chartData: DailyChartData[];
  hasPendingWorkRequest?: boolean;
  billableHoursThisMonth: number;
  nonBillableHoursThisMonth: number;
}

// Supporting types
export interface ActivityItem {
  id: string;
  type: 'user_created' | 'timesheet_approved' | 'project_created' | 'system_update';
  message: string;
  timestamp: string;
  userId?: string;
}

export interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  uptime: string;
  lastBackup: string;
  activeUsers: number;
}

export interface RoleCount {
  role: UserRole;
  count: number;
}

// Audit log types
export interface AuditLogEntry {
  id: number;
  user_id: number | null;
  user_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string | null;
}

export interface AuditLogsResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AuditLogFilters {
  entity_types: string[];
  actions: string[];
}

export interface ProjectOverview {
  projectId: string;
  projectName: string;
  clientName?: string;
  totalHours: number;
  budgetHours: number;
  utilizationPercentage: number;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  hoursThisWeek: number;
  pendingTimesheets: number;
  avatar?: string;
}

export interface ProjectProgress {
  projectId: string;
  projectName: string;
  completionPercentage: number;
  hoursLogged: number;
  hoursEstimated: number;
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

// Pagination
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================
// Asset Management Types
// ============================================

// Asset Category
export interface AssetCategory {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  is_active: boolean;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AssetCategoriesListResponse {
  categories: AssetCategory[];
  total: number;
}

// Asset State
export interface AssetState {
  id: number;
  name: string;
  color: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AssetStatesListResponse {
  states: AssetState[];
  total: number;
}

// Asset Location
export interface AssetLocation {
  id: number;
  name: string;
  address: string | null;
  is_active: boolean;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AssetLocationsListResponse {
  locations: AssetLocation[];
  total: number;
}

// Asset
export interface Asset {
  id: number;
  asset_id: string;
  category_id: number;
  category_name: string | null;
  state_id: number;
  state_name: string | null;
  state_color: string | null;
  location_id: number | null;
  location_name: string | null;
  assigned_to: number | null;
  assignee_name: string | null;
  // Basic Info
  serial_number: string | null;
  invoice_number: string | null;
  purchase_bill: string | null;
  purchase_date: string | null;
  value: number | null;
  warranty_expiry: string | null;
  // Hardware Specs
  host_name: string | null;
  cpu: string | null;
  ram: string | null;
  hdd: string | null;
  ssd: string | null;
  graphics_card: string | null;
  model_no: string | null;
  os: string | null;
  charger: string | null;
  // Network Info
  ip_address: string | null;
  wlan_mac: string | null;
  lan_mac: string | null;
  // Software/Security
  software_installed: string | null;
  password?: string | null;  // Only included when include_password=true
  // Other
  courier_user: string | null;
  courier_email: string | null;
  remark: string | null;
  is_active: boolean;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AssetMinimal {
  id: number;
  asset_id: string;
  category_name: string | null;
  state_name: string | null;
  state_color: string | null;
  location_name: string | null;
  assigned_to: number | null;
  assignee_name: string | null;
  model_no: string | null;
  serial_number: string | null;
  is_active: boolean;
}

export interface AssetsListResponse {
  assets: AssetMinimal[];
  total: number;
}

export interface AssetSummary {
  total: number;
  by_state: Record<string, { count: number; color: string }>;
  by_category: Record<string, number>;
}

// Asset Assignment
export interface AssetAssignment {
  id: number;
  asset_id: number;
  asset_code: string | null;
  assigned_to: number;
  employee_name: string | null;
  employee_email: string | null;
  assigned_by: number | null;
  assigner_name: string | null;
  assigned_at: string | null;
  expected_return: string | null;
  notes: string | null;
  returned_at: string | null;
  return_condition: string | null;
  return_notes: string | null;
  is_active: boolean;
}

export interface AssetHistoryResponse {
  asset_id: string;
  assignments: AssetAssignment[];
}

// Asset Log
export interface AssetLog {
  id: number;
  asset_id: number | null;
  asset_code: string | null;
  action: 'created' | 'updated' | 'deleted' | 'assigned' | 'returned' | 'state_changed';
  details: Record<string, any> | null;
  performed_by: number | null;
  performer_name: string | null;
  performed_at: string | null;
  ip_address: string | null;
}

export interface AssetLogsListResponse {
  logs: AssetLog[];
  total: number;
  pages: number;
  current_page: number;
  per_page: number;
}

export interface AssetLogAction {
  value: string;
  label: string;
}

// Create/Update DTOs
export interface CreateAssetCategoryData {
  name: string;
  code?: string;
  description?: string;
}

export interface UpdateAssetCategoryData {
  name?: string;
  code?: string;
  description?: string;
  is_active?: boolean;
}

export interface CreateAssetStateData {
  name: string;
  color?: string;
  description?: string;
  is_default?: boolean;
}

export interface UpdateAssetStateData {
  name?: string;
  color?: string;
  description?: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface CreateAssetLocationData {
  name: string;
  address?: string;
}

export interface UpdateAssetLocationData {
  name?: string;
  address?: string;
  is_active?: boolean;
}

export interface CreateAssetData {
  asset_id: string;
  category_id: number;
  state_id?: number;
  location_id?: number;
  serial_number?: string;
  invoice_number?: string;
  purchase_bill?: string;
  purchase_date?: string;
  value?: number;
  warranty_expiry?: string;
  host_name?: string;
  cpu?: string;
  ram?: string;
  hdd?: string;
  ssd?: string;
  graphics_card?: string;
  model_no?: string;
  os?: string;
  charger?: string;
  ip_address?: string;
  wlan_mac?: string;
  lan_mac?: string;
  software_installed?: string;
  password?: string;
  courier_user?: string;
  courier_email?: string;
  remark?: string;
}

export interface UpdateAssetData extends Partial<CreateAssetData> {
  is_active?: boolean;
}

export interface AssignAssetData {
  assigned_to: number;
  expected_return?: string;
  notes?: string;
}

export interface ReturnAssetData {
  return_condition?: string;
  return_notes?: string;
}
